import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Checkbox,
  FormControlLabel,
  FormHelperText,
  MenuItem,
  Select,
  SelectChangeEvent,
  Skeleton,
  Typography,
} from '@mui/material';
import { Grid } from '@mui/system';
import { TenantDomainWritable } from 'api';
import { globalAction } from 'common/actions';
import { COMPANY_USER_VALUE, FUND_ADMIN_VALUE, USER_ROLES } from 'common/constants/user';
import { useStore } from 'common/store';
import { UseStoreValues } from 'common/types/store';
import { useLayoutContext } from 'context/LayoutContext';
import { useGetTenantConfiguration, useSaveTenantMetadata } from 'pages/Firms/hooks';
import { useReadUserAccessLevel } from 'services/hooks';
import { EmailDomains, ServiceProviderInformation } from './components';
import CurrentSSOConfiguration from './components/CurrentSSOConfiguration/CurrentSSOConfiguration';
import { ResponsiveFormGroup, ResponsivePaper, ResponsiveTextField } from './SingleSignOn.styles';
import { CheckboxesState } from './SingleSignOn.types';

const VALID_ROLES = Object.values(USER_ROLES).filter(
  role => ![FUND_ADMIN_VALUE, COMPANY_USER_VALUE].includes(role.value)
);

const SingleSignOn = () => {
  const [{ firmInfo, user }, dispatch] = useStore() as unknown as UseStoreValues;
  const { updatePageActions } = useLayoutContext();
  const [metadataUrl, setMetadataUrl] = useState('');
  const [checkboxes, setCheckboxes] = useState<CheckboxesState>({
    enforcedSso: false,
    jitProvisioningEnabled: false,
  });
  const [emailDomains, setEmailDomains] = useState<TenantDomainWritable[]>([]);
  const [selectedFirmRole, setSelectedFirmRole] = useState<number | null>(null);
  const { errors, saveTenantMetadata } = useSaveTenantMetadata();

  const handleCheckboxes = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { name, checked } = event.target;
    setCheckboxes(prevState => ({
      ...prevState,
      [name]: checked,
    }));
  };
  const handleSelectFirmRole = (event: SelectChangeEvent) => {
    setSelectedFirmRole(Number(event.target.value));
  };

  const firmId = useMemo(() => firmInfo?.id ?? null, [firmInfo]);

  const { isStaffOrSuperuser, isAdminOrStaffOrSuperuser } = useReadUserAccessLevel({ user, firmInfo });

  const { tenantConfiguration, refetch } = useGetTenantConfiguration({
    firmId,
    setCheckboxes,
    setSelectedFirmRole,
    setEmailDomains,
  });

  const basicSSOConfigurationExists = useMemo(
    () => Boolean(tenantConfiguration?.idp_entity_id && tenantConfiguration?.idp_sso_url),
    [tenantConfiguration]
  );

  const saveMetadata = useCallback(async () => {
    if (firmId && selectedFirmRole !== null && updatePageActions) {
      dispatch(globalAction.showLoadingProgress(true));
      try {
        await saveTenantMetadata({
          url: metadataUrl.trim().length ? metadataUrl.trim() : null,
          firmId,
          enforcedSso: checkboxes.enforcedSso,
          jitProvisioningEnabled: checkboxes.jitProvisioningEnabled,
          selectedFirmRole,
          domains: emailDomains,
        });
        // Refetch tenant configuration to get the latest data
        await refetch(firmId);
      } finally {
        dispatch(globalAction.showLoadingProgress(false));
      }
    }
  }, [
    firmId,
    selectedFirmRole,
    updatePageActions,
    dispatch,
    saveTenantMetadata,
    metadataUrl,
    checkboxes,
    emailDomains,
    refetch,
  ]);

  useEffect(() => {
    if (firmId && updatePageActions && isAdminOrStaffOrSuperuser) {
      updatePageActions({
        mainAction: {
          id: 'firm-single-sign-on-save',
          label: 'Save',
          isActive: false,
          callback: saveMetadata,
        },
      });
    }
  }, [firmId, metadataUrl, updatePageActions, saveMetadata, isAdminOrStaffOrSuperuser]);

  if (!tenantConfiguration && !selectedFirmRole) {
    return <Skeleton variant="rectangular" width="100%" height={200} />;
  }

  return (
    <Grid container spacing={4} sx={{ mb: 10, flexDirection: { xs: 'column', lg: 'row' } }}>
      {/* Left column: main SSO settings */}
      <Grid size={{ xs: 12, lg: 6 }}>
        <Typography variant="h6" gutterBottom>
          Single Sign-On Settings
        </Typography>
        {basicSSOConfigurationExists && <CurrentSSOConfiguration tenantConfiguration={tenantConfiguration!} />}
        {!basicSSOConfigurationExists && (
          <Typography variant="body1" gutterBottom>
            Enter your Identity Provider metadata URL to configure single sign-on.
          </Typography>
        )}
        <ResponsiveTextField
          required
          id="metadata-url"
          type="url"
          label="Metadata URL"
          variant="outlined"
          size="small"
          value={metadataUrl}
          onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
            setMetadataUrl(event.target.value);
          }}
          error={Boolean(errors?.length)}
          helperText={errors ? errors.join('. ') : ''}
        />

        <ResponsiveFormGroup sx={{ mt: 3 }}>
          <FormControlLabel
            control={
              <Checkbox
                checked={checkboxes.enforcedSso}
                onChange={handleCheckboxes}
                name="enforcedSso"
                disabled={!isAdminOrStaffOrSuperuser}
              />
            }
            label="Enforced SSO"
          />
          <FormControlLabel
            control={
              <Checkbox
                checked={checkboxes.jitProvisioningEnabled}
                onChange={handleCheckboxes}
                name="jitProvisioningEnabled"
                disabled={!isAdminOrStaffOrSuperuser}
              />
            }
            label="Just In Time (JIT) Provisioning"
          />
        </ResponsiveFormGroup>
        <ResponsiveFormGroup>
          <Typography variant="h4" gutterBottom sx={{ mt: 2 }}>
            Default firm role
          </Typography>
          <Select
            labelId="default-firm-role-select"
            id="default-firm-role-select"
            value={(selectedFirmRole ?? 0).toString()}
            label="Firm role for new users"
            onChange={handleSelectFirmRole}>
            {VALID_ROLES.map(role => (
              <MenuItem key={role.value} value={role.value}>
                {role.label}
              </MenuItem>
            ))}
          </Select>
          <FormHelperText>Firm role provisioned for new users</FormHelperText>
        </ResponsiveFormGroup>
        <EmailDomains
          emailDomains={emailDomains}
          setEmailDomains={setEmailDomains}
          isStaffOrSuperuser={isStaffOrSuperuser}
        />
      </Grid>
      {/* Right column: ServiceProviderInformation */}
      <Grid size={{ xs: 12, lg: 6 }} sx={{ mt: { xs: 2, lg: '1.25em' } }}>
        <ResponsivePaper elevation={4}>
          <ServiceProviderInformation
            acsUrl={tenantConfiguration!.acs_url as string}
            spEntityId={tenantConfiguration!.sp_entity_id as string}
          />
        </ResponsivePaper>
      </Grid>
    </Grid>
  );
};

export default SingleSignOn;
